import React from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { PropertyBadge } from './PropertyBadge';
import { SignalLine } from './ExperienceProtection';
import { AssignPopover } from './AssignPopover';
import { buildTaskTitle } from '../lib/taskTitles';
import { 
  UserPlus, RefreshCw, CheckCircle, Play, Clock, CalendarClock, ArrowUpRight
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

/*
 Standard Task Card Layout (unified across Today + Daily Run + Inbox + Email)
 Order: Title → Meta row → Impact line (urgent only) → Next → By + Owner → Actions
*/

const IMPACT_MAP = {
  access: "Guest can't access the property right now.",
  comfort: "Comfort issue — guest experience affected.",
  trust: "Trust at risk — guest confidence dropping.",
  revenue: "Revenue at risk — booking may be lost.",
  safety: "Safety concern — needs immediate attention.",
};

const getRelativeTime = (dateStr) => {
  if (!dateStr) return null;
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: false }) + ' ago';
  } catch {
    return null;
  }
};

const formatDueTime = (dateStr) => {
  if (!dateStr) return 'End of day';
  try {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return 'End of day';
  }
};

// Department / role slug → human-readable label (keeps cards consistent across surfaces).
const OWNER_LABELS = {
  front_desk:      'Front Desk',
  housekeeping:    'Housekeeping',
  maintenance:     'Maintenance',
  transport:       'Transport / Driver',
  concierge:       'Concierge',
  reservations:    'Reservations',
  manager_on_duty: 'Manager on Duty',
};
const formatOwnerLabel = (raw) => {
  if (!raw) return '';
  return OWNER_LABELS[String(raw).toLowerCase()] || String(raw);
};

export const TaskCard = ({ 
  item, 
  onHandle, 
  onMarkDone, 
  onAssign, 
  onPreventRepeat,
  onPullIntoToday,
  onSetNextTouch,
  onCloseOut,
  onKeepActive,
  variant = 'default', // 'default' | 'handle_now' | 'due_today' | 'waiting' | 'scheduled' | 'carry_over'
}) => {
  const isUrgent = variant === 'handle_now' || item.priority === 'Now' || item.priority === 'HANDLE_NOW';
  const isDone = item.status === 'Done' || item.status === 'DONE' || item.status === 'Closed out';
  const hasRepeatSignal = item.signal_type === 'repeat' || item.signal_type === 'risk';
  const guest = item.guest || item.guest_name_text;
  const channel = item.channel || item.source_channel;
  const updatedAt = item.last_updated_at || item.lastTouch || item.last_touched_at;

  // Build outcome-first title via shared Title Template Dictionary.
  // If caller pre-built a title via item.title, prefer that.
  const title = item.title || buildTaskTitle({
    category: item.category || item._raw?.category,
    summary: item.summary,
    guest_name: guest,
  });

  return (
    <div 
      className={`p-4 bg-white rounded-lg border transition-colors group ${
        variant === 'carry_over' ? 'border-amber-200 bg-amber-50/50' :
        isUrgent ? 'border-red-100 hover:border-red-200' :
        'border-stone-100 hover:border-stone-200'
      }`}
      data-testid="task-card"
    >
      {/* Title (H4) — outcome-first, visually dominant */}
      <h4 className="text-sm font-semibold text-stone-900 leading-snug line-clamp-2">
        {title}
      </h4>

      {/* Meta row — small, muted, single line */}
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-1.5 text-[11px] text-stone-400 leading-tight">
        <PropertyBadge propertyId={item.propertyId} variant="inline" size="sm" />
        {channel && <><span className="text-stone-300">&middot;</span><span>{channel}</span></>}
        {guest ? (
          <><span className="text-stone-300">&middot;</span><span>{guest}</span></>
        ) : (
          <><span className="text-stone-300">&middot;</span><span className="italic">Guest: Not linked</span></>
        )}
        {updatedAt && (
          <><span className="text-stone-300">&middot;</span><span>Updated: {getRelativeTime(updatedAt) || updatedAt}</span></>
        )}
        {isUrgent && <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-red-50 text-red-600 border-red-200 ml-1">Urgent</Badge>}
      </div>

      {/* Signal line — quiet, under meta */}
      <SignalLine signal_type={item.signal_type} impact_area={item.impact_area} fix_route={item.fix_route} />

      {/* Impact line — only for Handle Now / urgent */}
      {isUrgent && item.impact_area && (
        <p className="text-xs text-red-600/80 mt-1.5 font-medium">
          Impact: {IMPACT_MAP[item.impact_area] || `${(item.impact_area || '').charAt(0).toUpperCase() + (item.impact_area || '').slice(1)} issue — needs attention.`}
        </p>
      )}

      {/* Next step */}
      {(item.nextStep || item.notes) && (
        <p className="text-xs text-stone-500 mt-2">
          <span className="font-semibold text-stone-700">Next:</span> {item.nextStep || item.notes}
        </p>
      )}

      {/* By + Owner + Route-to row */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-stone-500">
        <span>
          <span className="font-medium text-stone-600">By:</span> {formatDueTime(item.dueAt || item.due)}
        </span>
        <span>
          <span className="font-medium text-stone-600">Owner:</span>{' '}
          {item.owner ? (
            <span className="text-stone-700">{formatOwnerLabel(item.owner)}</span>
          ) : (
            <span className="text-amber-600 italic">Unassigned</span>
          )}
        </span>
        {item.route_to && (
          <span>
            <span className="font-medium text-stone-600">Route to:</span>{' '}
            <span className="inline-flex items-center gap-1 text-[#3F617E]">
              <ArrowUpRight className="h-3 w-3" />
              {formatOwnerLabel(item.route_to)}
            </span>
          </span>
        )}
        {!item.owner && item.dueAt && (
          <span className="text-amber-600 text-[10px]">Assign by {formatDueTime(item.dueAt)}</span>
        )}

        {/* Carry-over: next touch */}
        {variant === 'carry_over' && (
          <span>
            <span className="font-medium text-stone-600">Next touch:</span>{' '}
            {item.next_touch_at ? (
              formatDueTime(item.next_touch_at)
            ) : (
              <span className="text-amber-600 font-medium">Not set</span>
            )}
          </span>
        )}

        {/* Waiting: next touch */}
        {variant === 'waiting' && (
          <span>
            <span className="font-medium text-stone-600">Next touch:</span>{' '}
            {item.next_touch_at ? (
              formatDueTime(item.next_touch_at)
            ) : (
              <span className="text-amber-600 font-medium">Next touch not set</span>
            )}
          </span>
        )}
      </div>

      {/* Actions row — consistent buttons at bottom */}
      {!isDone && (
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-2.5 border-t border-stone-100">
          {/* Primary action */}
          {isUrgent && onHandle && (
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-xs h-7 rounded-full px-3" onClick={onHandle} data-testid="handle-btn">
              Handle
            </Button>
          )}
          {!isUrgent && variant === 'default' && item.status === 'New' && onHandle && (
            <Button size="sm" className="bg-[#3F617E] hover:bg-[#345470] text-white text-xs h-7 rounded-full px-3" onClick={onHandle} data-testid="take-this-btn">
              Take this
            </Button>
          )}
          {variant === 'due_today' && item.status === 'New' && onHandle && (
            <Button size="sm" className="bg-[#3F617E] hover:bg-[#345470] text-white text-xs h-7 rounded-full px-3" onClick={onHandle} data-testid="take-this-btn">
              Take this
            </Button>
          )}
          {variant === 'scheduled' && onPullIntoToday && (
            <Button size="sm" variant="outline" className="text-xs h-7 rounded-full px-3" onClick={onPullIntoToday} data-testid="pull-today-btn">
              <ArrowUpRight className="h-3 w-3 mr-1" /> Pull into today
            </Button>
          )}
          {variant === 'scheduled' && onSetNextTouch && (
            <Button size="sm" variant="outline" className="text-xs h-7 rounded-full px-3" onClick={onSetNextTouch} data-testid="set-next-touch-btn">
              <CalendarClock className="h-3 w-3 mr-1" /> Set next touch
            </Button>
          )}
          {variant === 'carry_over' && onKeepActive && (
            <Button size="sm" variant="outline" className="text-xs h-7 rounded-full px-3 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={onKeepActive} data-testid="keep-active-btn">
              Keep active
            </Button>
          )}
          {variant === 'carry_over' && onCloseOut && (
            <Button size="sm" variant="outline" className="text-xs h-7 rounded-full px-3" onClick={onCloseOut} data-testid="close-out-btn">
              <CheckCircle className="h-3 w-3 mr-1" /> Close out
            </Button>
          )}

          {/* Secondary: Assign — opens a popover to set Owner + Route to */}
          {onAssign && (
            <AssignPopover
              task={item}
              onConfirm={(payload) => onAssign(payload)}
              trigger={
                <Button size="sm" variant="outline" className="text-xs h-7 rounded-full px-3 text-stone-600" data-testid="assign-btn">
                  <UserPlus className="h-3 w-3 mr-1" /> Assign
                </Button>
              }
            />
          )}

          {/* Mark done */}
          {onMarkDone && variant !== 'carry_over' && variant !== 'scheduled' && (
            <Button size="sm" variant="ghost" className="text-xs h-7 px-2 text-stone-500" onClick={onMarkDone} data-testid="done-btn">
              <CheckCircle className="h-3 w-3 mr-1" /> Done
            </Button>
          )}

          {/* Text link: Prevent repeat */}
          {hasRepeatSignal && onPreventRepeat && (
            <button
              className="text-[11px] text-stone-400 hover:text-stone-600 underline underline-offset-2 ml-auto transition-colors"
              onClick={onPreventRepeat}
              data-testid="prevent-repeat-link"
            >
              Prevent repeat
            </button>
          )}
        </div>
      )}

      {/* Done state */}
      {isDone && (
        <div className="flex items-center gap-2 mt-3 pt-2.5 border-t border-stone-100">
          <Badge className="bg-green-100 text-green-700 text-xs">Closed out</Badge>
          {item.closed_at && <span className="text-[10px] text-stone-400">at {formatDueTime(item.closed_at)}</span>}
        </div>
      )}
    </div>
  );
};
